import { useState, useEffect, useCallback, useMemo } from 'react';
import { futebolRepository } from '../../data/futebolRepository';
import { supabase } from '@/shared/lib/supabase';
import type { FutebolJogoDia, FutebolPartidaAtiva, FutebolPartidaHistoricoItem } from '@/shared/types';

function partidaAtualParaHistorico(
  telaId: string,
  partida: FutebolPartidaAtiva,
  jogo: FutebolJogoDia,
): FutebolPartidaHistoricoItem {
  return {
    id:               `ativa-${partida.fixture_id}`,
    tela_id:          telaId,
    fixture_id:       partida.fixture_id!,
    placar_casa:      partida.placar_casa,
    placar_visitante: partida.placar_visitante,
    stats:            partida.stats,
    incidents:        partida.incidents,
    funfacts:         partida.funfacts,
    lineup:           partida.lineup,
    ultimo_evento:    partida.ultimo_evento,
    encerrado_em:     partida.updated_at,
    time_casa:        jogo.time_casa,
    time_visitante:   jogo.time_visitante,
    escudo_casa:      jogo.escudo_casa,
    escudo_visitante: jogo.escudo_visitante,
    liga_nome:        jogo.liga_nome,
    horario:          jogo.horario,
  };
}

export function useHistoricoJogosDia(
  telaId: string,
  partidaAtual: FutebolPartidaAtiva | null,
  jogoAtual: FutebolJogoDia | null,
) {
  const [itens, setItens] = useState<FutebolPartidaHistoricoItem[]>([]);
  const [loading, setLoading] = useState(true);

  const recarregar = useCallback(() => {
    if (!telaId) return;
    futebolRepository
      .listarHistoricoDia(telaId)
      .then(setItens)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [telaId]);

  useEffect(() => {
    setLoading(true);
    recarregar();
  }, [recarregar]);

  useEffect(() => {
    if (!telaId) return;

    const channel = supabase
      .channel(`futebol-historico-${telaId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'futebol_partida_historico', filter: `tela_id=eq.${telaId}` },
        () => { recarregar(); },
      )
      .subscribe();

    const onVisible = () => {
      if (document.visibilityState === 'visible') recarregar();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      supabase.removeChannel(channel);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [telaId, recarregar]);

  const itensMesclados = useMemo(() => {
    const lista = [...itens];
    if (
      partidaAtual?.tempo_jogo === 'FT' &&
      partidaAtual.fixture_id != null &&
      jogoAtual &&
      !lista.some((h) => h.fixture_id === partidaAtual.fixture_id)
    ) {
      lista.unshift(partidaAtualParaHistorico(telaId, partidaAtual, jogoAtual));
    }
    return lista;
  }, [itens, partidaAtual, jogoAtual, telaId]);

  return { itens: itensMesclados, loading };
}
